import { DOMAIN_ROUTER_TRANSPORTS } from '../../../../packages/domain-core/dist/esm/entities/domain-router-transport.js';
import {
  ystnjSemanticOperationManifest,
} from '@triedge/rtk-abstractions';
import {
  ACADEMICS_MUTATION_OPERATION_IDS,
  ACADEMICS_QUERY_OPERATION_IDS,
} from '@triedge/academics-domain';
import {
  ATTENDANCE_MUTATION_OPERATION_IDS,
  ATTENDANCE_QUERY_OPERATION_IDS,
} from '@triedge/attendance-domain/router-contract';
import {
  ADMIN_GOVERNANCE_MUTATION_OPERATION_IDS,
  ADMIN_GOVERNANCE_QUERY_OPERATION_IDS,
  ASSISTANT_INTEGRATIONS_MUTATION_OPERATION_IDS,
  ASSISTANT_INTEGRATIONS_QUERY_OPERATION_IDS,
  ASSISTANT_PLATFORM_MUTATION_OPERATION_IDS,
  ASSISTANT_PLATFORM_QUERY_OPERATION_IDS,
  CAMPS_MUTATION_OPERATION_IDS,
  CAMPS_QUERY_OPERATION_IDS,
  DONATIONS_MUTATION_OPERATION_IDS,
  DONATIONS_QUERY_OPERATION_IDS,
  NOTIFICATIONS_MUTATION_OPERATION_IDS,
  NOTIFICATIONS_QUERY_OPERATION_IDS,
  PAYMENTS_MUTATION_OPERATION_IDS,
  PAYMENTS_QUERY_OPERATION_IDS,
} from '@triedge/domain-core';
import {
  ADMISSIONS_MUTATION_OPERATION_IDS,
  ADMISSIONS_QUERY_OPERATION_IDS,
} from '@triedge/admissions-domain';
import {
  FACULTY_MUTATION_OPERATION_IDS,
  FACULTY_QUERY_OPERATION_IDS,
} from '@triedge/faculty-domain';
import {
  FINANCIAL_AID_MUTATION_OPERATION_IDS,
  FINANCIAL_AID_QUERY_OPERATION_IDS,
} from '@triedge/financial-aid-domain';
import {
  MESSAGING_MUTATION_OPERATION_IDS,
  MESSAGING_QUERY_OPERATION_IDS,
  PARENT_PORTAL_QUERY_OPERATION_IDS,
} from '@triedge/parent-portal-domain';
import {
  PAYROLL_MUTATION_OPERATION_IDS,
  PAYROLL_QUERY_OPERATION_IDS,
} from '@triedge/payroll-domain';
import {
  STAFF_SETTINGS_MUTATION_OPERATION_IDS,
  STAFF_SETTINGS_QUERY_OPERATION_IDS,
} from '@triedge/staff-settings-domain';
import {
  STUDENTS_FAMILIES_MUTATION_OPERATION_IDS,
  STUDENTS_FAMILIES_QUERY_OPERATION_IDS,
} from '@triedge/students-families-domain';
import type {
  DomainRouterKnownMutationName,
  DomainRouterKnownQueryName,
} from '../state/domainRouterOperationRegistry';

const SUBSTITUTES_QUERY_OPERATION_IDS = [
  'getSubstituteDashboard',
  'listOpenAbsences',
  'listSubstitutePool',
  'listSubstituteSchedule',
  'getSubstitutePayroll',
] as const;

const SUBSTITUTES_MUTATION_OPERATION_IDS = [
  'assignSubstitute',
] as const;

const TIP_A_TEACHER_QUERY_OPERATION_IDS = [
  'getTipParentCatalog',
  'listTipParentHistory',
  'getTipSchoolOverview',
  'getTipProgramSettings',
  'listTipNotifications',
] as const;

const TIP_A_TEACHER_MUTATION_OPERATION_IDS = [
  'updateTipProgramSettings',
  'sendTipNotificationTest',
] as const;

export type RoutedTransportTarget = {
  routerName: DomainRouterTransportRouterName;
  transportField: string;
};
type DomainRouterTransportDefinition = (typeof DOMAIN_ROUTER_TRANSPORTS)[keyof typeof DOMAIN_ROUTER_TRANSPORTS];
type DomainRouterTransportRouterName = keyof typeof DOMAIN_ROUTER_TRANSPORTS;
const DIRECT_MUTATION_OPERATION_NAMES = new Set<string>([
  'provisionSelfPersona',
]);

const buildManifestRoutedTransportTargets = (): {
  queries: Readonly<Partial<Record<DomainRouterKnownQueryName, RoutedTransportTarget>>>;
  mutations: Readonly<Partial<Record<DomainRouterKnownMutationName, RoutedTransportTarget>>>;
} => {
  const queries: Partial<Record<DomainRouterKnownQueryName, RoutedTransportTarget>> = {};
  const mutations: Partial<Record<DomainRouterKnownMutationName, RoutedTransportTarget>> = {};

  for (const entry of ystnjSemanticOperationManifest) {
    const routerName = String(entry.runtime?.routerName || '').trim() as DomainRouterTransportRouterName;
    const operationId = String(entry.runtime?.operationId || '').trim();
    const operationKind = String(entry.runtime?.operationKind || '').trim();
    const transportField = String(entry.graphql?.transport?.transportField || '').trim();

    if (!routerName || !operationId || !transportField) {
      continue;
    }

    const target = {
      routerName,
      transportField,
    };

    if (operationKind === 'query') {
      queries[operationId as DomainRouterKnownQueryName] = target;
    }
    if (operationKind === 'mutation') {
      mutations[operationId as DomainRouterKnownMutationName] = target;
    }
  }

  return {
    queries: Object.freeze(queries),
    mutations: Object.freeze(mutations),
  };
};

const MANIFEST_ROUTED_TRANSPORTS = buildManifestRoutedTransportTargets();
const MANIFEST_QUERY_OPERATION_IDS = new Set(Object.keys(MANIFEST_ROUTED_TRANSPORTS.queries));
const MANIFEST_MUTATION_OPERATION_IDS = new Set(Object.keys(MANIFEST_ROUTED_TRANSPORTS.mutations));

const buildRoutedTransportTarget = (
  routerName: DomainRouterTransportRouterName,
  operationKind: 'query' | 'mutation'
): RoutedTransportTarget => {
  const routerTransport = DOMAIN_ROUTER_TRANSPORTS[routerName];
  if (!routerTransport) {
    throw new Error(`Unknown domain router transport "${routerName}" for ${operationKind} operation mapping`);
  }
  return {
    routerName,
    transportField: routerTransport[
      operationKind === 'query' ? 'queryField' : 'mutationField'
    ],
  };
};

const mapOperationIdsToTransport = <TOperationId extends string>(
  operationIds: readonly TOperationId[],
  target: RoutedTransportTarget,
  manifestOwnedOperationIds?: ReadonlySet<string>,
): Readonly<Record<TOperationId, RoutedTransportTarget>> =>
  Object.freeze(
    Object.fromEntries(
      operationIds
        .filter((operationId) => !manifestOwnedOperationIds?.has(operationId))
        .map((operationId) => [operationId, target])
    ) as Record<
      TOperationId,
      RoutedTransportTarget
    >
  );

const mapFallbackQueryOperationIdsToTransport = <TOperationId extends string>(
  operationIds: readonly TOperationId[],
  target: RoutedTransportTarget,
): Readonly<Record<TOperationId, RoutedTransportTarget>> =>
  mapOperationIdsToTransport(operationIds, target, MANIFEST_QUERY_OPERATION_IDS);

const mapFallbackMutationOperationIdsToTransport = <TOperationId extends string>(
  operationIds: readonly TOperationId[],
  target: RoutedTransportTarget,
): Readonly<Record<TOperationId, RoutedTransportTarget>> =>
  mapOperationIdsToTransport(operationIds, target, MANIFEST_MUTATION_OPERATION_IDS);

export const QUERY_ROUTED_TRANSPORT_BY_OPERATION: Readonly<
  Partial<Record<DomainRouterKnownQueryName, RoutedTransportTarget>>
> = {
  ...MANIFEST_ROUTED_TRANSPORTS.queries,
  ...mapFallbackQueryOperationIdsToTransport(
    ADMIN_GOVERNANCE_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('identityGovernanceApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    ACADEMICS_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainAcademicsApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    ASSISTANT_INTEGRATIONS_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('assistantIntegrationsApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    ASSISTANT_PLATFORM_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('assistantPlatformContractApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    ATTENDANCE_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainAttendanceApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    CAMPS_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainCampsApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    ADMISSIONS_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainAdmissionsApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    FACULTY_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainFacultyApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    FINANCIAL_AID_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainFinancialAidApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    MESSAGING_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainMessagingApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    NOTIFICATIONS_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainNotificationsApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    PARENT_PORTAL_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('parentPortalRouter', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    PAYMENTS_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainPaymentsApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    SUBSTITUTES_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainSubstitutesApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    PAYROLL_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainPayrollApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    DONATIONS_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainDonationsApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    STAFF_SETTINGS_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainSettingsApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    STUDENTS_FAMILIES_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainStudentsFamiliesApi', 'query')
  ),
  ...mapFallbackQueryOperationIdsToTransport(
    TIP_A_TEACHER_QUERY_OPERATION_IDS,
    buildRoutedTransportTarget('domainTipATeacherApi', 'query')
  ),
  resolveFamilyForAdmissionsApplication: buildRoutedTransportTarget('domainAdmissionsApi', 'query'),
  getAdmissionsTuitionConfiguration: buildRoutedTransportTarget('domainAdmissionsApi', 'query'),
  getApplicationDocumentDownloadUrl: buildRoutedTransportTarget('domainAdmissionsApi', 'query'),
};

export const MUTATION_ROUTED_TRANSPORT_BY_OPERATION: Readonly<
  Partial<Record<DomainRouterKnownMutationName, RoutedTransportTarget>>
> = {
  ...MANIFEST_ROUTED_TRANSPORTS.mutations,
  ...mapFallbackMutationOperationIdsToTransport(
    ADMIN_GOVERNANCE_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('identityGovernanceApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    ACADEMICS_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainAcademicsApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    ASSISTANT_INTEGRATIONS_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('assistantIntegrationsApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    ASSISTANT_PLATFORM_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('assistantPlatformContractApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    ATTENDANCE_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainAttendanceApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    CAMPS_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainCampsApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    ADMISSIONS_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainAdmissionsApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    FACULTY_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainFacultyApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    FINANCIAL_AID_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainFinancialAidApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    MESSAGING_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainMessagingApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    NOTIFICATIONS_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainNotificationsApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    PAYMENTS_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainPaymentsApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    SUBSTITUTES_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainSubstitutesApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    PAYROLL_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainPayrollApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    DONATIONS_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainDonationsApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    STAFF_SETTINGS_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainSettingsApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    STUDENTS_FAMILIES_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainStudentsFamiliesApi', 'mutation')
  ),
  ...mapFallbackMutationOperationIdsToTransport(
    TIP_A_TEACHER_MUTATION_OPERATION_IDS,
    buildRoutedTransportTarget('domainTipATeacherApi', 'mutation')
  ),
  upsertAdmissionsTuitionConfiguration: buildRoutedTransportTarget('domainAdmissionsApi', 'mutation'),
  generateTuitionContract: buildRoutedTransportTarget('domainAdmissionsApi', 'mutation'),
  deleteStaffUser: buildRoutedTransportTarget('domainSettingsApi', 'mutation'),
  sendTuitionContract: buildRoutedTransportTarget('domainAdmissionsApi', 'mutation'),
};

export const resolveRoutedQueryTransport = (
  operationName: DomainRouterKnownQueryName | string
): RoutedTransportTarget | undefined => QUERY_ROUTED_TRANSPORT_BY_OPERATION[operationName];

export const resolveRoutedMutationTransport = (
  operationName: DomainRouterKnownMutationName | string
): RoutedTransportTarget | undefined => {
  if (DIRECT_MUTATION_OPERATION_NAMES.has(String(operationName).trim())) {
    return undefined;
  }
  return MUTATION_ROUTED_TRANSPORT_BY_OPERATION[operationName];
};
